/* eslint-disable indent */
import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { renderBusinessAvatar, renderUserAvatar } from '../../Util/data';
import { connectionStatusCodes } from '../../Util/connectionStatuses';
import { iconMappings } from '../../Util/iconMappings';
import Loader from '../Loader/Loader';
import AttachmentRevamp from '../AttachmentRevamp/AttachmentRevamp';
import StarRating from './StarRating';

const UserInfo = ({
  showProfileDetails,
  conversation,
  attachments,
  acceptConnection,
  rejectConnection,
  isProposalUser,
  locale,
  acceptingNudge,
  rejectingNudge,
  additionalClasses,
  recipientUserIdLastChar,
  displayRateBoxToEntrepreneur,
  addInvestorRating,
  ratingSuccessTitle,
  ratingSuccessText,
  investorSignupUpdateFeature
}) => {
  const { t } = useTranslation();
  const [investorRating, setInvestorRating] = useState(0);
  const [investorRatingFeedback, setInvestorRatingFeedback] = useState(null);
  const [displayRatingBox, setDisplayRatingBox] = useState(true);

  const handleInvestorRatingFeedback = (event) => {
    setInvestorRatingFeedback(event.target.value);
  };

  const addRating = (rating, feedback) => {
    addInvestorRating(rating, conversation.connectionId, feedback);
  };

  return !conversation ? (
    <div
      className={`${
        additionalClasses ||
        (!showProfileDetails ? 'hidden' : 'xl:block hidden')
      } xl:w-1/4 w-full lg:px-4 h-full transition-all`}
    >

      <div className="w-full py-10 flex flex-col justify-center gap-3 items-center">
        <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse mb-2" />
        <div className="w-full flex flex-col justify-center items-center">
          <div className="w-1/2 h-6 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
          <div className="w-1/2 h-3 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
        </div>
        <div className="w-3/4 h-4 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
        <div className="w-3/4 h-2 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />

        <div className="w-3/4 h-4 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
        <div className="w-3/4 h-8 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
        <div className="w-3/4 h-6 mx-auto rounded-md bg-gray-200 animate-pulse mb-2" />
      </div>
    </div>
  ) : (
    <div
      className={`${
        additionalClasses ||
        (!showProfileDetails ? 'hidden' : 'xl:block hidden')
      } xl:w-1/4 w-full lg:px-4 h-full transition-all`}
    >
      <div className="w-full h-full overflow-auto scrollbar-hide">
        {!isProposalUser ? (
          <div className="w-full py-4 flex flex-col justify-center gap-3 items-center">
            {conversation.proposal.logoUri ? (
              <img
                src={conversation.proposal.logoUri}
                alt={conversation.proposal.title}
                className="rounded-md w-24 h-24 object-center object-cover"
              />
            ) : (
              renderBusinessAvatar(
                conversation.proposal.title,
                null,
                `bg-${recipientUserIdLastChar}`
              )
            )}
            <div className="w-full flex flex-col justify-center items-center">
              <p className="text-gray-800 text-xl">
                {conversation.proposal.title}
              </p>
              <a
                href={conversation.proposal.proposalPageUri}
                target="_blank"
                className="font-normal text-sm underline text-blue-600 my-3"
                rel="noreferrer"
              >
                {t('auth.inbox.viewPitch')}
              </a>
              <p className="text-gray-700 text-sm leading-6 my-3">
                {conversation.proposal.description}
              </p>
            </div>
            {conversation.status.code ===
            connectionStatusCodes.pendingAcceptance ? (
              <>
                <p className="text-sm text-gray-500">
                  {t('auth.inbox.wantToConnect')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    return acceptConnection(
                      conversation.connectionUserActions.acceptConnectionUri
                    );
                  }}
                  className="block w-full mt-3 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  {acceptingNudge ? (
                    <Loader title="Loading" />
                  ) : (
                    t('auth.inbox.interested')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    return rejectConnection(
                      conversation.connectionUserActions.rejectConnectionUri
                    );
                  }}
                  className="block w-full px-5 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
                >
                  {rejectingNudge ? (
                    <Loader title="Loading" />
                  ) : (
                    t('auth.inbox.ignore')
                  )}
                </button>
              </>
            ) : null}
          </div>
        ) : (
          <div className="w-full py-6 flex flex-col justify-center gap-5 items-center">
            <div className="w-full flex flex-col justify-center gap-3 items-center">
              {conversation.interlocutor.logoUri ? (
                <img
                  src={conversation.interlocutor.logoUri}
                  alt={`${conversation.interlocutor.name.firstName} ${conversation.interlocutor.name.lastName}`}
                  className="w-20 h-20 rounded-full object-center object-cover"
                />
              ) : (
                renderUserAvatar(
                  `${conversation.interlocutor.name.firstName} ${conversation.interlocutor.name.lastName}`,
                  'w-24 h-24',
                  `bg-${recipientUserIdLastChar}`
                )
              )}
              <div className="w-full flex flex-col justify-center items-center">
                <p
                  className={`${
                    investorSignupUpdateFeature
                      ? 'text-gray-700 text-xl font-semibold leading-none'
                      : 'text-gray-800 text-xl'
                  }`}
                >
                  {`${conversation.interlocutor.name.firstName} ${conversation.interlocutor.name.lastName}`}
                </p>
                {!investorSignupUpdateFeature ? (
                  <span className="text-gray-600 text-base">
                    <span className="material-icons text-base text-blue-500 mr-1">
                      location_on
                    </span>
                    {conversation.interlocutor.townName
                      ? `${conversation.interlocutor.townName}, `
                      : null}
                    {t(
                      `common.countryName.${conversation.interlocutor.countryCode}`
                    )}
                </span>
                ) : null}
              </div>
              {conversation.interlocutor.profileUri ? (
                <a
                  href={conversation.interlocutor.profileUri}
                  target="_blank"
                  className={`${investorSignupUpdateFeature ? 'font-normal text-md underline text-blue-600 leading-none' : 'font-normal text-sm underline text-blue-600'}`}
                  rel="noreferrer"
                >
                  {t('auth.inbox.viewProfile')}
                </a>
              ) : null}
              {conversation.interlocutor.backgroundDescription && investorSignupUpdateFeature ? (
                <p className="mt-2">{conversation.interlocutor.backgroundDescription}</p>
              ) : null}
              {conversation.interlocutor.minInvestment &&
              conversation.interlocutor.minInvestment ? (
                <>
                  <span
                    className={`${investorSignupUpdateFeature ? 'text-gray-700 font-semibold text-lg mt-2 leading-none' : 'text-xs text-gray-800 inline-block mt-4'}`}
                  >
                    {t('auth.inbox.investmentRange')}
                  </span>
                  <span
                    className={`${investorSignupUpdateFeature ? 'text-2xl leading-none text-gray-700' : 'text-xl text-gray-800 text-base'}`}
                  >
                    {t('currency', {
                      val: conversation.interlocutor.minInvestment,
                      formatParams: {
                        val: {
                          currency: conversation.interlocutor.currencyCode,
                          locale: locale.replace(/_/g, '-'),
                          maximumFractionDigits: 0
                        }
                      }
                    })}
                    &nbsp;-&nbsp;
                    {t('currency', {
                      val: conversation.interlocutor.maxInvestment,
                      formatParams: {
                        val: {
                          currency: conversation.interlocutor.currencyCode,
                          locale: locale.replace(/_/g, '-'),
                          maximumFractionDigits: 0
                        }
                      }
                    })}
                  </span>
                </>
              ) : null}
              {conversation.interlocutor.emailAddress && investorSignupUpdateFeature ? (
                <div className="flex items-center space-x-2 mt-3">
                  <span className="material-icons text-gray-700">email</span>
                  <span className="text-gray-700 font-semibold text-[16px]">{conversation.interlocutor.emailAddress}</span>
                </div>
              ) : null}
              {conversation.interlocutor.phone && investorSignupUpdateFeature ? (
                <div className="flex items-center space-x-2">
                  <span className="material-icons text-gray-700">phone</span>
                  <span className="text-gray-700 font-semibold text-[16px]">{conversation.interlocutor.phone}</span>
                </div>
              ) : null}

              {conversation.interlocutor.userLinks && investorSignupUpdateFeature ? (
                <div className="flex space-x-2 mt-4">
                  {conversation.interlocutor.userLinks.map(({ url, typeCode }) => {
                    const iconName = iconMappings[typeCode];
                    const cleanedValue = url.replace(/\s|%20/g, "");
                    const normalizedUrl =
                        cleanedValue.startsWith("http://") || cleanedValue.startsWith("https://")
                        ? cleanedValue
                        : `https://${cleanedValue}`;
                    return (
                      <a
                        key={typeCode}
                        href={normalizedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
                        style={{ width: 44, height: 44 }}
                        title={url}
                      >
                        <i className={`text-gray-700 ${iconName} fa-2x`}></i>
                        <span className="sr-only">{url}</span>
                      </a>
                    );
                  })}
                </div>
              ) : null}

            </div>

            {displayRateBoxToEntrepreneur && displayRatingBox && (
              <div className="w-full bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center text-center gap-2">
                {ratingSuccessTitle && ratingSuccessText && (
                  <button
                    type="button"
                    onClick={() => {
                      return setDisplayRatingBox(false);
                    }}
                    className="relative ml-auto w-4 h-4 inline-flex items-center justify-center text-gray-600"
                  >
                    <span className="material-icons">close</span>
                  </button>
                )}
                <h4 className="text-gray-800 font-semibold text-lg">
                  {ratingSuccessTitle || t('auth.inbox.rating.rate')}{' '}
                  {conversation.interlocutor.name.firstName}
                </h4>
                <p className="text-gray-700 text-md font-normal">
                  {ratingSuccessText || t('auth.inbox.rating.rateDescription')}
                </p>
                {!ratingSuccessText && !ratingSuccessTitle && (
                  <>
                    <StarRating
                      rating={investorRating}
                      updateRating={(rating) => {
                        return setInvestorRating(rating);
                      }}
                    />
                    <p className="mr-auto">
                      <span className="font-semibold">
                        {t('auth.inbox.rating.feedback')}
                      </span>{' '}
                      ({t('auth.inbox.rating.optional')})
                    </p>
                    <textarea
                      className="py-2 px-3 border border-gray-300 rounded-md w-full focus:outline outline-blue-600 focus:border-blue-600 h-20"
                      onChange={handleInvestorRatingFeedback}
                      maxLength={255}
                    />
                    <button
                      type="submit"
                      className="w-full rounded-md focus:outline-none bg-blue-600 hover:bg-blue-500 py-2 px-4 text-white text-base font-semibold"
                      onClick={() => {
                        return addRating(
                          investorRating,
                          investorRatingFeedback
                        );
                      }}
                    >
                      {t('auth.inbox.rating.submit')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {attachments?.length > 0 && (
          <div className="w-full pb-10 max-h-72 overflow-x-hidden overflow-y-auto">
            <h4 className="font-semibold text-lg text-gray-800 mb-3 sticky top-0 bg-white z-10">
              {t('auth.inbox.mediaAndAttachments')}
            </h4>
            <div className="w-full flex flex-wrap">
              {attachments.map((attachment, index) => {
                // eslint-disable-next-line react/no-array-index-key
                return <AttachmentRevamp key={index} attachment={attachment} />;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInfo;

UserInfo.propTypes = {
  showProfileDetails: PropTypes.bool,
  conversation: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Array)
  ]),
  attachments: PropTypes.instanceOf(Object),
  acceptConnection: PropTypes.func,
  rejectConnection: PropTypes.func,
  isProposalUser: PropTypes.bool,
  locale: PropTypes.string,
  acceptingNudge: PropTypes.bool,
  rejectingNudge: PropTypes.bool,
  additionalClasses: PropTypes.oneOf([PropTypes.string, null]),
  recipientUserIdLastChar: PropTypes.string,
  displayRateBoxToEntrepreneur: PropTypes.bool,
  addInvestorRating: PropTypes.func,
  ratingSuccessText: PropTypes.oneOf([PropTypes.string, null]),
  ratingSuccessTitle: PropTypes.oneOf([PropTypes.string, null]),
  investorSignupUpdateFeature: PropTypes.bool
};

UserInfo.defaultProps = {
  showProfileDetails: true,
  conversation: null,
  attachments: null,
  acceptConnection: () => {},
  rejectConnection: () => {},
  isProposalUser: false,
  locale: '',
  acceptingNudge: false,
  rejectingNudge: false,
  additionalClasses: null,
  recipientUserIdLastChar: '0',
  displayRateBoxToEntrepreneur: false,
  addInvestorRating: () => {},
  ratingSuccessText: null,
  ratingSuccessTitle: null,
  investorSignupUpdateFeature: false
};
